// src/api/api.js

import { auth } from "../firebase";

// Radice del backend, letta dall'ambiente (frontend/.env.*).
//
// ⚠️ Nessun valore di ripiego verso la produzione, di proposito: in beta e in
// sviluppo la variabile è volutamente vuota finché il backend beta non esiste.
// Con un default la beta finirebbe a parlare col backend di produzione, cioè
// con dati reali di minori. Vedi CLAUDE.md §2.
//
// Con valore vuoto le chiamate partono relative all'origine (es. "/users"):
// in sviluppo passano dal proxy di Create React App, altrove falliscono in
// modo visibile — che è il comportamento voluto.
export const API_ROOT = process.env.REACT_APP_API_ROOT || "";

if (!API_ROOT) {
  console.warn(
    "[api] REACT_APP_API_ROOT non impostata: le chiamate al backend partiranno " +
    "relative all'origine. Atteso in beta finché il backend beta non esiste."
  );
}

/**
 * Intestazioni di autenticazione per il backend.
 *
 * Se c'e' un utente Firebase loggato allega "Authorization: Bearer <ID token>";
 * il token e' fornito e rinnovato automaticamente dall'SDK Firebase.
 * Se nessuno e' loggato la chiamata parte senza header: sara' il backend a
 * rispondere 401 sugli endpoint riservati.
 *
 * ⚠️ Il token non va mai scritto nei log ne' nella query string.
 *
 * @param {Object} base intestazioni di partenza da conservare
 * @returns {Promise<Object>} intestazioni da passare a fetch
 */
export async function authHeaders(base = {}) {
  const utente = auth.currentUser;
  if (!utente) return { ...base };

  try {
    const idToken = await utente.getIdToken();
    return { ...base, Authorization: `Bearer ${idToken}` };
  } catch (err) {
    // Si registra solo il codice d'errore, mai il token.
    console.warn(
      "[api] ID token Firebase non disponibile:",
      err && err.code ? err.code : "errore"
    );
    return { ...base };
  }
}

/**
 * L'errore di una risposta NON ok.
 *
 * Il MESSAGGIO è identico a quello di prima, carattere per carattere: alcuni
 * chiamanti lo leggono a tentoni (`err.message?.includes("409")`) e cambiarlo
 * li romperebbe in silenzio. Quello che si aggiunge sono due proprietà:
 *
 *   • `status` — il codice HTTP, così chi chiama può distinguere un RIFIUTO
 *     del server (400, 409, 500) da un GUASTO di rete, dove nessuna risposta
 *     è mai arrivata e `status` resta assente. Senza questa distinzione si
 *     finisce a dire «controlla i tuoi dati» a chi ha solo la linea caduta —
 *     il difetto della vecchia login, che non si vuole ereditare;
 *   • `body` — il corpo grezzo della risposta, da cui il chiamante può
 *     leggere il `{ error: "..." }` delle rotte v2 senza rifare la fetch.
 *
 * @param {string} path percorso chiamato, per il messaggio
 * @param {number} status codice HTTP della risposta
 * @param {string} testo corpo della risposta, grezzo
 * @returns {Error} errore da lanciare
 */
function erroreDiRisposta(path, status, testo) {
  const err = new Error(`API ${path} failed: ${status} ${testo}`);
  err.status = status;
  err.body = testo;
  // D8B (RD-166): il 401 del backend nasce SOLO da `requireAuth` (token
  // assente, malformato o scaduto) e dalle due difese di `requireRole*` senza
  // `requireAuth` — mai da un permesso negato, che è 403. Il messaggio resta
  // quello di sempre (TeamAssignment lo legge con un'espressione regolare):
  // si aggiunge un segnale, e chi chiama mostra SESSION_EXPIRED_MESSAGE.
  // Nessun reindirizzo e nessun logout: decisione del committente, 30/9.
  if (status === 401) err.sessionExpired = true;
  return err;
}

// Il testo a schermo della sessione scaduta, unico per tutta l'app.
export const SESSION_EXPIRED_MESSAGE =
  "Your session has expired. Please sign in again.";

// D8B (RD-166): tetti di attesa. Senza, una richiesta che si impunta lascia
// «Saving…» acceso per sempre. 30 s per le chiamate JSON; 120 s per gli upload
// multipart (foto e documenti da telefono, su linea lenta), perché un tetto
// stretto spezzerebbe un caricamento legittimo. Compromesso dichiarato: un
// upload davvero fermo si scopre solo dopo due minuti.
const TIMEOUT_JSON_MS = 30000;
const TIMEOUT_UPLOAD_MS = 120000;

// D8B-FIX: tetto LUNGO, da chiedere per singola chiamata
// (`api.post(path, body, { timeoutMs: TIMEOUT_LUNGO_MS })`) sulle sole
// operazioni che il server porta a termine oltre i 30 s: i solleciti (fino a
// 100 email SMTP spedite una alla volta), l'invio in chat (letture + push a
// ogni lettore, tutto il club nell'annuncio), l'assistente, l'import. Troncarle
// farebbe dichiarare fallita un'azione già eseguita e invitare a ripeterla.
// 5 minuti: margine sui 100 invii sequenziali anche a 2 s l'uno.
export const TIMEOUT_LUNGO_MS = 300000;

// Il testo a schermo di un INVIO scaduto: il server può averlo già eseguito,
// quindi non si invita a ripetere.
export const TIMEOUT_INVIO_MESSAGE =
  "The server did not reply in time. It may have already been sent — please check before trying again.";

/**
 * Esegue `fetch` con un tetto di attesa e ne legge la risposta.
 *
 * Il timer copre anche la preparazione delle intestazioni (il rinnovo del
 * token) e la lettura del corpo: il segnale di `AbortController` interrompe
 * pure lo stream della risposta. Allo scadere nasce un errore SENZA `status` —
 * come un guasto di rete, che è ciò che i chiamanti già sanno trattare — con
 * `timeout: true` per chi volesse distinguerlo. Il messaggio non contiene un
 * codice a tre cifre, così `codiceHttp` di TeamAssignment non lo scambia per
 * una risposta del server.
 *
 * @param {string} path percorso chiamato
 * @param {number} tetto millisecondi di attesa massima
 * @param {() => Promise<Object>} preparaOpzioni opzioni di fetch (senza signal)
 * @param {(res: Response) => Promise<*>} leggi lettura della risposta ok
 */
async function conTetto(path, tetto, preparaOpzioni, leggi) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), tetto);

  try {
    const opzioni = await preparaOpzioni();
    const res = await fetch(`${API_ROOT}${path}`, {
      ...opzioni,
      signal: controller.signal,
    });

    if (!res.ok) {
      const text = await res.text();
      throw erroreDiRisposta(path, res.status, text);
    }

    return await leggi(res);
  } catch (err) {
    // Solo questo modulo annulla: un AbortError è sempre il tetto scaduto.
    if (controller.signal.aborted && err && err.name === "AbortError") {
      const scaduto = new Error(`API ${path} failed: timeout`);
      scaduto.timeout = true;
      throw scaduto;
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

async function apiRequest(path, options = {}) {
  // `timeoutMs` si toglie qui: è un'opzione di questo modulo, non di fetch.
  const { headers: intestazioniExtra, timeoutMs, ...altreOpzioni } = options;
  const tetto =
    typeof timeoutMs === "number" && Number.isFinite(timeoutMs) && timeoutMs > 0
      ? timeoutMs
      : TIMEOUT_JSON_MS;

  return conTetto(
    path,
    tetto,
    async () => ({
      credentials: "include",
      ...altreOpzioni,
      headers: await authHeaders({
        "Content-Type": "application/json",
        ...intestazioniExtra,
      }),
    }),
    (res) => {
      // Se non c’è body (204)
      if (res.status === 204) return null;

      return res.json();
    }
  );
}

async function apiFormRequest(path, formData) {
  return conTetto(
    path,
    TIMEOUT_UPLOAD_MS,
    async () => ({
      method: "POST",
      credentials: "include",
      // ⚠️ Nessun Content-Type: lo imposta il browser con il boundary del
      // multipart. Solo l'header di autenticazione, quando c'e' un utente.
      headers: await authHeaders(),
      body: formData,
    }),
    (res) => res.json()
  );
}


// ======= EXPORT PUBBLICI =======

export const api = {
  get: (path) => apiRequest(path),
  // D8B-FIX: terzo argomento FACOLTATIVO, `{ timeoutMs }`. Chi non lo passa
  // manda la richiesta di prima, col tetto di 30 s.
  post: (path, body, opzioni) =>
    apiRequest(path, {
      method: "POST",
      body: JSON.stringify(body),
      timeoutMs: opzioni ? opzioni.timeoutMs : undefined,
    }),
  put: (path, body) =>
    apiRequest(path, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

   patch: (path, body) =>
    apiRequest(path, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  // ⚠️ IL CORPO È FACOLTATIVO, e chi non lo passa manda esattamente la
  // richiesta di prima: nessun chiamante esistente cambia comportamento.
  // Serve alla sola cancellazione che chiede una CONFERMA DIGITATA
  // (DELETE /clinic-guests/:userId, cantiere CLINICS C2), dove la parola non
  // può viaggiare nella query string — che finisce nei log dei proxy e nella
  // cronologia — e non c'è un corpo di risorsa da mandare altrove.
  del: (path, body) =>
    apiRequest(path, {
      method: "DELETE",
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),
  postForm: (path, formData) =>
    apiFormRequest(path, formData),
};